import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import AcademicCountdown from "../../DynamicComponent/AcademicCountdown";

const FALLBACK_DASHBOARD = {
  childName: "Andrei Bloom",
  grade: "Nilai 5",
  section: "A",
  registrationNo: "STU2024001",
  attendancePercent: 88,
  upcomingExams: 3,
  recentLeaves: [
    { leaveId: "LV001", fromDate: "12/03/2025", toDate: "13/03/2025", reason: "Medical", status: "Approved" },
    { leaveId: "LV002", fromDate: "25/03/2025", toDate: "25/03/2025", reason: "Personal", status: "Pending" },
    { leaveId: "LV003", fromDate: "10/04/2025", toDate: "11/04/2025", reason: "Family Function", status: "Rejected" },
  ],
  progressSummary: [
    { subject: "Tamil", marks: 85 },
    { subject: "English", marks: 78 },
    { subject: "Math", marks: 92 },
    { subject: "Science", marks: 80 },
    { subject: "Social", marks: 74 },
  ],
};

const LEAVE_STATUS_CONFIG = {
  Approved:  { color: "#4caf50", bg: "#e8f5e9" },
  Pending:   { color: "#ff9800", bg: "#fff3e0" },
  Rejected:  { color: "#f44336", bg: "#fdecea" },
  Open:      { color: "#2196f3", bg: "#e3f2fd" },
};

const StatusChip = ({ status }) => {
  const cfg = LEAVE_STATUS_CONFIG[status] || { color: "#8F95B2", bg: "#f3f4f6" };
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: "11px",
        borderRadius: "20px",
      }}
    />
  );
};

const QUICK_ACTIONS = [
  { label: "Student Profile", icon: <PersonIcon />, color: "#244C77", path: "/parent/student-profile" },
  { label: "Progress Report", icon: <BarChartIcon />, color: "#4caf50", path: "/parent/progress-report" },
  { label: "Attendance", icon: <CheckCircleIcon />, color: "#2196f3", path: "/parent/attendance" },
  { label: "Leave / Complaint", icon: <AssignmentIcon />, color: "#ff9800", path: "/parent/complaint" },
  { label: "Raise Complaint", icon: <ContactSupportIcon />, color: "#336599", path: "/parent/complaint" },
  { label: "Withdrawal", icon: <ExitToAppIcon />, color: "#f44336", path: "/parent/withdrawal" },
];

const ParentDashboard = () => {
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadDashboard = useCallback(async () => {
    try {
      const res = await fetchData("parent/dashboard");
      if (res?.data) {
        setDashData(res.data);
      } else {
        setDashData(FALLBACK_DASHBOARD);
      }
    } catch (_) {
      setDashData(FALLBACK_DASHBOARD);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const d = dashData || FALLBACK_DASHBOARD;

  const childInitials = d.childName
    ? d.childName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "AB";

  const kpiCards = [
    {
      label: "Child's Name",
      value: d.childName || "—",
      color: "#244C77",
      bg: "#ede9fb",
      icon: <PersonIcon sx={{ fontSize: "22px" }} />,
    },
    {
      label: "Grade / Section",
      value: `${d.grade || "Nilai 5"} - ${d.section || "A"}`,
      color: "#2196f3",
      bg: "#e3f2fd",
      icon: <SchoolIcon sx={{ fontSize: "22px" }} />,
    },
    {
      label: "Attendance",
      value: `${d.attendancePercent ?? 88}%`,
      color: "#4caf50",
      bg: "#e8f5e9",
      icon: <CheckCircleIcon sx={{ fontSize: "22px" }} />,
    },
    {
      label: "Upcoming Exams",
      value: d.upcomingExams ?? 3,
      color: "#ff9800",
      bg: "#fff3e0",
      icon: <EventNoteIcon sx={{ fontSize: "22px" }} />,
    },
  ];

  const leaves = d.recentLeaves || FALLBACK_DASHBOARD.recentLeaves;
  const progress = d.progressSummary || FALLBACK_DASHBOARD.progressSummary;
  const maxMarks = 100;

  return (
    <Box sx={{ padding: "1.5rem", fontFamily: "'Inter', 'Roboto', sans-serif", backgroundColor: "#F8F9FA", minHeight: "100vh" }}>
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <AcademicCountdown />

      {/* Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "26px", fontWeight: "bold", color: "#1a1a2e" }}>
          Welcome,{" "}
          <span style={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Parent Portal
          </span>
        </Typography>
        <Typography sx={{ color: "#8F95B2", fontSize: "14px", mt: 0.3 }}>
          Monitor your child's academic progress and activities
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* ── KPI Cards ── */}
        {kpiCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "14px",
                  backgroundColor: card.bg,
                  color: card.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a2e", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {card.value}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#8F95B2", mt: 0.2 }}>{card.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}

        {/* ── Left Column ── */}
        <Grid item xs={12} md={8}>
          {/* Recent Leave Status */}
          <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", mb: 3 }}>
            <Box
              sx={{
                background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                px: 3,
                py: 2,
              }}
            >
              <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#fff" }}>
                Recent Leave Requests
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.75)", mt: 0.3 }}>
                Status of recent leave applications
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                    {["Leave ID", "From", "To", "Reason", "Status"].map((h) => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 600, fontSize: "12px", color: "#6B7280", py: 1.5, borderBottom: "1px solid #F3F4F6" }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaves.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#9CA3AF", fontSize: "13px" }}>
                        No leave records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    leaves.map((row, idx) => (
                      <TableRow key={idx} sx={{ "&:hover": { backgroundColor: "#FAFAFA" }, "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.2 }}>{row.leaveId || `LV${String(idx + 1).padStart(3, "0")}`}</TableCell>
                        <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.2 }}>{row.fromDate || row.from || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.2 }}>{row.toDate || row.to || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.2 }}>{row.reason || "—"}</TableCell>
                        <TableCell sx={{ py: 1.2 }}><StatusChip status={row.status} /></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Progress Summary — CSS bar chart */}
          <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
            <Box sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", px: 3, py: 2 }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#fff" }}>
                Recent Progress Summary
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "rgba(255,255,255,0.75)", mt: 0.3 }}>
                Subject-wise marks overview
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {progress.map((item, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>{item.subject}</Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#244C77" }}>{item.marks}/{maxMarks}</Typography>
                  </Box>
                  <Box sx={{ height: "10px", borderRadius: "6px", backgroundColor: "#EDE9FB", overflow: "hidden" }}>
                    <Box
                      sx={{
                        height: "100%",
                        width: `${Math.min((item.marks / maxMarks) * 100, 100)}%`,
                        background: idx % 2 === 0
                          ? "linear-gradient(90deg, #244C77, #4A7BA8)"
                          : "linear-gradient(90deg, #4A7BA8, #c4b8fc)",
                        borderRadius: "6px",
                        transition: "width 0.6s ease",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* ── Right Column ── */}
        <Grid item xs={12} md={4}>
          {/* Child Info Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
              mb: 3,
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                p: 3,
                textAlign: "center",
              }}
            >
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  mx: "auto",
                  mb: 1.5,
                  border: "3px solid rgba(255,255,255,0.4)",
                  fontSize: "24px",
                  fontWeight: "bold",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                }}
              >
                {childInitials}
              </Avatar>
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "17px" }}>
                {d.childName || "—"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", mt: 0.4 }}>
                {d.grade || "Nilai 5"} &nbsp;·&nbsp; Section {d.section || "A"}
              </Typography>
              <Chip
                label={`Reg: ${d.registrationNo || "STU2024001"}`}
                size="small"
                sx={{
                  mt: 1.5,
                  backgroundColor: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
            </Box>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={1}>
                {[
                  { label: "Attendance", value: `${d.attendancePercent ?? 88}%`, color: "#4caf50" },
                  { label: "Exams", value: d.upcomingExams ?? 3, color: "#ff9800" },
                  { label: "Leaves", value: leaves.length, color: "#244C77" },
                ].map((stat) => (
                  <Grid item xs={4} key={stat.label}>
                    <Box sx={{ textAlign: "center", p: 1, borderRadius: "10px", backgroundColor: "#F9FAFB" }}>
                      <Typography sx={{ fontWeight: "bold", fontSize: "15px", color: stat.color }}>{stat.value}</Typography>
                      <Typography sx={{ fontSize: "10px", color: "#6B7280", mt: 0.2 }}>{stat.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>

          {/* Quick Actions */}
          <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
            <Box sx={{ background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", px: 3, py: 2 }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#fff" }}>Quick Actions</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={1.5}>
                {QUICK_ACTIONS.map((action, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Button
                      fullWidth
                      onClick={() => navigate(action.path)}
                      startIcon={
                        <Box sx={{ color: action.color, display: "flex", alignItems: "center" }}>
                          {action.icon}
                        </Box>
                      }
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#374151",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        py: 1.2,
                        px: 1.5,
                        backgroundColor: "#FAFAFA",
                        "&:hover": {
                          backgroundColor: `${action.color}12`,
                          borderColor: action.color,
                          color: action.color,
                        },
                      }}
                    >
                      {action.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ParentDashboard;
